import type { CreateBasketItem } from '@accesso/cc-ecomm-api';
import { PlatformLocation } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, Injector, input, Renderer2 } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { filter, first, of, switchMap } from 'rxjs';

import { ProductTemplateRendererComponent } from '@accesso/cc-ui';
import { ProductModel } from '@accesso/cc-ui/model';
import { JsonApiService } from '@accesso/ng-json-api';

import { MetaDataService } from '../../feature/browser/meta-data.service';
import { CartStore } from '../../feature/cart/cart.store';
import { MerchantStore } from '../../feature/merchant/merchant.store';
import { BreadcrumbsComponent } from '../../feature/routing/breadcrumbs/breadcrumbs.component';
import { seoTitle } from '../../feature/seo/seo-utils';

@Component({
	selector: 'app-product',
	imports: [BreadcrumbsComponent, ProductTemplateRendererComponent],
	styleUrl: './product.component.scss',
	template: `
		<div class="flex flex-1 justify-center">
			<div class="content flex flex-col">
				@if (product().state().loaded) {
					<app-breadcrumbs class="py-4 px-[72px]" [items]="[{ label: product().name() }]" />
				}
				<ecom-product-template-renderer
					[productId]="id()"
					[basket]="basket() ?? undefined"
					[canonicalUrl]="canonicalUrl"
					[loading]="cartLoading()"
					(addItemsToCart)="addItemsToCart($event)"
				/>
			</div>
		</div>
	`,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductComponent {
	/**
	 * The product identifier from the route parameter.
	 */
	id = input.required<string>();

	private readonly router = inject(Router);
	private readonly injector = inject(Injector);
	private readonly merchantStore = inject(MerchantStore);
	private readonly platformLocation = inject(PlatformLocation);
	private readonly jsonApi = inject(JsonApiService);
	private readonly cartStore = inject(CartStore);
	private readonly renderer2 = inject(Renderer2);
	private readonly metadata = inject(MetaDataService);

	canonicalUrl: string = this.platformLocation.protocol + '//' + this.platformLocation.hostname + this.platformLocation.pathname;

	basket = this.cartStore.basket;

	cartLoading = computed(() => this.cartStore.creating() || this.basket()?.state().loading || this.basket()?.state().updating || false);

	product = computed(() => this.jsonApi.findOne(ProductModel, this.id()));

	constructor() {
		effect(() => {
			const product = this.product();
			if (!product.state().loaded) return;

			const image = product.settings()?.images?.find((image) => image.primary)?.src;
			const div = this.renderer2.createElement('div');

			div.innerHTML = product.name();
			const name = div.textContent;

			div.innerHTML = product.description();
			const description = div.textContent;

			this.metadata.setMetaData({
				title: seoTitle(name, this.merchantStore.merchant()?.name()),
				description,
				image: image ? `${image}?width=512&height=288&crop=true` : null,
			});
		});
	}

	/**
	 * Called when a page template or product wants to add items to the cart
	 * @param newItems The items to add
	 */
	addItemsToCart(newItems: CreateBasketItem[]): void {
		this.cartStore
			.addItems(newItems)
			.pipe(
				first(),
				switchMap((basket) => (!basket ? of(null) : toObservable(basket.state, { injector: this.injector }))),
				filter((state) => (!state ? true : !state.loading && !state.updating))
			)
			.subscribe({
				next: (basket) => {
					if (basket) this.router.navigate(['cart']);
				},
			});
	}
}
